import { DataBlock, DynamicValue } from "@/core/datablocks/types/blocks";
import {
  DataField,
  DataTemplate,
  FieldType,
} from "@/core/datablocks/types/templates";
import { useMemo } from "react";
import { getDisplayComponentForField } from "@/core/datablocks/fields/renderers";

export function formatDataBlockValueByType(
  value: DynamicValue,
  field: DataField,
): string[] | string | undefined {
  if (value === undefined) {
    return undefined;
  }

  switch (field.type) {
    case FieldType.TEXT:
    case FieldType.DATE:
      if (field.multiple) {
        return value as string[];
      }
      return value as string;
    case FieldType.SELECT: {
      if (field.multiple) {
        const values = (value as number[]) || [];
        const selectedOptions = field.options.filter((opt) =>
          values.includes(opt.id),
        );
        return selectedOptions.map((opt) => opt.label) as string[];
      }

      const selectedOption = field.options?.find((opt) => opt.id === value);
      return selectedOption?.label;
    }
    default:
      return undefined;
  }
}

export function getDataBlockFieldValue<T = DynamicValue>(
  dataBlock: DataBlock | undefined,
  fieldMapping: keyof DataBlock["fieldsMapping"],
  template?: DataTemplate,
  formatter?: (value: DynamicValue, field: DataField) => T,
): T | undefined {
  if (!dataBlock) return undefined;
  const field = dataBlock.fieldValues.find(
    (value) => value.field === dataBlock.fieldsMapping[fieldMapping],
  );

  if (!field) return undefined;

  if (formatter && template) {
    const templateField = template.templateFields.find(
      (tf) => tf.id === field.field,
    );

    if (!templateField) return undefined;

    return formatter(field.value as DynamicValue, templateField);
  }

  return field.value as T;
}

export function useDataBlockField(
  dataBlock: DataBlock | undefined,
  fieldName: keyof DataBlock["fieldsMapping"],
  template?: DataTemplate,
  options: {
    rawValue?: boolean;
    componentOnly?: boolean;
  } = {},
) {
  const rawValue = useMemo(
    () => getDataBlockFieldValue(dataBlock, fieldName),
    [dataBlock, fieldName],
  );

  const field = template?.templateFields.find(
    (tf) => tf.id === dataBlock?.fieldsMapping[fieldName],
  );

  const formattedValue = useMemo(() => {
    if (!field || options.rawValue) return rawValue;
    return formatDataBlockValueByType(rawValue as DynamicValue, field);
  }, [rawValue, field, options.rawValue]);

  if (options.componentOnly && field) {
    return getDisplayComponentForField(field, rawValue);
  }

  return formattedValue;
}
